// Closed-trade card — card-view counterpart of a MyTrades table row. Works
// for both the Audit and Trader tabs: MyTrades maps either row shape into
// the shared TradeCardVM before handing it here.

import {
  CardShell,
  CardHeader,
  CardBody,
  CardFooter,
  StatLine,
  MarketTypeChip,
  SideBadge,
  Pill,
} from './cardKit';
import { fmtCents, fmtQty, fmtUsd, fmtUsdSigned, fmtAge } from './cardFormat';
import { maskPnl, maskPnlColor } from '../../lib/pnlMask';
import { VenueChip } from '../atoms/VenueChip';
import type { TradingVenue } from '../../lib/venues';
import { formatUsSignedUsd } from '../../lib/polymarketUs/format';

export type TradeAction = 'OPEN' | 'SELL' | 'SETTLED' | 'REJECTED';

/** #1227 — a Polymarket US row's exact decimal text beside the numeric fields
 *  (the #1226 `qtyText` / `feesText` convention): quantities and USD amounts
 *  render as this text, the numbers only sort and sum. A null money text is a
 *  figure the evidence cannot establish ("—"), its reason in `dashTitle`. */
export interface UsTradeFacts {
  qtyText: string;
  feesText: string | null;
  totalInText: string | null;
  cashOutText: string | null;
  /** Signed decimal text, unformatted, so the P&L cells keep their own sign rule. */
  netPnlText: string | null;
  /** A NO fill's price is 1 − YES: averages over NO fills carry the `*` marker. */
  entryProvisional: boolean;
  exitProvisional: boolean;
  dashTitle: string | null;
  /** Why a realized row's net is unknown, for the venue headline's notes (#1031's two counts). */
  pending: 'payout' | 'incomplete' | null;
}

export interface TradeCardVM {
  key: string;
  venue: TradingVenue;
  ticker: string;
  title: string;
  subtitle: string;
  eventStartLabel: string | null;
  side: 'yes' | 'no';
  team: string | null;
  /** #964 — an NFL ladder prop's stat, shown as its own label beside (or
   *  under) the one-line side badge (see cardFormat.titleParts). Absent
   *  otherwise. */
  marketType?: string | null;
  action: TradeAction;
  qty: number;
  /** Average entry (buy) price, cents. */
  entryCents: number | null;
  /** Average exit (sell / settlement) price, cents. */
  exitCents: number | null;
  feesDollars: number | null;
  /** Total dollars committed at entry (cost basis + open fees). Card↔table
   *  parity: the audit table shows this as "Total In" / "Cost"; surfacing
   *  it here so a trader scanning closed trades sees what they actually
   *  spent without having to multiply qty × entry mentally. Null when fee
   *  data is incomplete. */
  totalInDollars: number | null;
  /** Total dollars received at close (proceeds − close fees). Card↔table
   *  parity: the audit table shows this as "Cash Out" / "Total Out".
   *  Null for OPEN rows (position not yet closed) or when fee data is
   *  incomplete. */
  cashOutDollars: number | null;
  netPnlDollars: number | null;
  closedAtMs: number;
  isRejected: boolean;
  rejectedReason: string | null;
  /** Polymarket Risk / To Win / Juice per the client accounting convention
   *  (src/lib/pricing/polyEconomics.ts): risk = total in (fee-inclusive),
   *  to win = qty − (total in − fees), juice = odds of risk vs to-win. The
   *  VM builder owns the derivation so every consumer shows one set of
   *  numbers; Kalshi rows omit these (pending #114 rebate semantics). */
  riskDollars?: number | null;
  toWinDollars?: number | null;
  juice?: string | null;
  /** Polymarket US rows only (#1227). */
  us?: UsTradeFacts;
}

export const ACTION_STYLE: Record<TradeAction, string> = {
  SETTLED: 'bg-blue-500/20 text-blue-300',
  SELL: 'bg-violet-500/20 text-violet-300',
  OPEN: 'bg-amber-400/20 text-amber-300',
  REJECTED: 'bg-red-500/20 text-red-300',
};

export function TradeCard({ vm, hidePnl = false }: { vm: TradeCardVM; hidePnl?: boolean }) {
  const pnlClass = maskPnlColor(
    hidePnl,
    vm.netPnlDollars == null
      ? 'text-muted-foreground'
      : vm.netPnlDollars > 0
        ? 'text-positive'
        : vm.netPnlDollars < 0
          ? 'text-negative'
          : 'text-foreground',
  );

  return (
    <CardShell
      title={vm.rejectedReason ?? (vm.us?.dashTitle ? `${vm.ticker} — ${vm.us.dashTitle}` : vm.ticker)}
      viewAnchor={vm.key}
    >
      <CardHeader
        leftBadges={
          <>
            {/* #127: cards from both venues interleave in one grid, so the
                venue label must be explicit on the card itself. */}
            <VenueChip venue={vm.venue} />
            <Pill className={ACTION_STYLE[vm.action]}>{vm.action}</Pill>
            <SideBadge side={vm.side} team={vm.team} />
            <MarketTypeChip marketType={vm.marketType} />
          </>
        }
        title={vm.title}
        subtitle={vm.subtitle}
      />

      <CardBody>
        {/* #1227: a US row's quantities and money are its exact decimal text. */}
        <StatLine label="qty" value={vm.us?.qtyText ?? fmtQty(vm.qty)} />
        <StatLine label="entry (raw)" value={fmtCents(vm.entryCents) + (vm.us?.entryProvisional ? '*' : '')} />
        <StatLine label="exit" value={fmtCents(vm.exitCents) + (vm.us?.exitProvisional ? '*' : '')} />
        <StatLine label="start" value={vm.eventStartLabel || '—'} />
        <StatLine label="fees" value={maskPnl(hidePnl, vm.us ? vm.us.feesText ?? '—' : fmtUsd(vm.feesDollars))} />
        {vm.venue === 'polymarket' ? (
          <>
            {/* Polymarket final-card Risk / To Win / Juice come precomputed
                on the VM (polyEconomics convention: risk fee-inclusive, to
                win fee-free, fee drag carried by the juice). Venue-scoped
                until #114 settles Kalshi's expected-vs-credited rebates. */}
            <StatLine label="risk" value={maskPnl(hidePnl, fmtUsd(vm.riskDollars))} />
            <StatLine label="to win" value={maskPnl(hidePnl, fmtUsd(vm.toWinDollars))} />
            {/* Juice is odds (a price ratio), not the trader's P&L — stays
                visible like the entry/exit ¢ prices. */}
            <StatLine label="juice" value={vm.juice ?? '—'} />
          </>
        ) : (
          <StatLine
            label="total in"
            value={maskPnl(hidePnl, vm.us ? vm.us.totalInText ?? '—' : fmtUsd(vm.totalInDollars))}
          />
        )}
        {/* Cash out = close proceeds - closing fees (what actually returned
            to the account). Null for OPEN rows or incomplete fee data. */}
        <StatLine
          label="cash out"
          value={maskPnl(hidePnl, vm.us ? vm.us.cashOutText ?? '—' : fmtUsd(vm.cashOutDollars))}
        />
      </CardBody>

      <CardFooter>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-muted-foreground text-[11px]">
            {vm.isRejected
              ? (vm.rejectedReason ?? 'rejected')
              : vm.closedAtMs > 0
                ? `${vm.action === 'OPEN' ? 'opened' : 'closed'} ${fmtAge(vm.closedAtMs)} ago`
                : `${vm.action === 'OPEN' ? 'opened' : 'closed'}, time unknown`}
          </span>
          <span className="text-xs">
            <span className="text-muted-foreground">net P&L </span>
            <span className={`font-mono text-sm font-semibold ${pnlClass}`}>
              {maskPnl(hidePnl, vm.us ? (vm.us.netPnlText === null ? '—' : formatUsSignedUsd(vm.us.netPnlText)) : vm.netPnlDollars == null ? '—' : fmtUsdSigned(vm.netPnlDollars))}
            </span>
          </span>
        </div>
      </CardFooter>
    </CardShell>
  );
}
